/* global React, Icon */
// ============================================================
// PLUMME 2.0 — Canvas PDF viewer (pdf.js renders pages to canvas;
// Chrome blocks <object>/<iframe> PDF embeds, so we rasterize instead)
// Controls: page nav + jump, zoom, rotate, download.
// ============================================================
const PDFJS_WORKER = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const PDFJS_SCRIPT = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
// SRI: ny versjon = nye hasher (openssl dgst -sha384 -binary | openssl base64 -A).
const PDFJS_SCRIPT_INTEGRITY = 'sha384-/1qUCSGwTur9vjf/z9lmu/eCUYbpOTgSjmpbMQZ1/CtX2v/WcAIKqRv+U1DUCG6e';
const PDFJS_WORKER_INTEGRITY = 'sha384-SnzOobpRMLXZ52iJvZm/C0fYw0OQemTXzTjIsdsfMcrCtCEe9qgzxTd3RSklO5x2';

// En Worker kan ikke bære integrity=. Vi henter bytene, sjekker hashen selv og starter
// workeren fra en blob-URL, slik at en endret CDN-fil aldri får parse brukerens PDF-er.
// Feiler sjekken, avviser loftet og viewer-en faller tilbake til 'embed'.
let pdfWorkerUrlLoading = null;
function loadVerifiedPdfWorkerUrl() {
  if (pdfWorkerUrlLoading) return pdfWorkerUrlLoading;
  pdfWorkerUrlLoading = (async () => {
    const res = await fetch((window.__resources && window.__resources.pdfWorker) || PDFJS_WORKER, { credentials: 'omit' });
    if (!res.ok) throw new Error('pdfjs-worker-missing');
    const buf = await res.arrayBuffer();
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-384', buf));
    let binary = '';
    for (const byte of digest) binary += String.fromCharCode(byte);
    if (`sha384-${btoa(binary)}` !== PDFJS_WORKER_INTEGRITY) throw new Error('pdfjs-worker-integrity-mismatch');
    return URL.createObjectURL(new Blob([buf], { type: 'text/javascript' }));
  })().catch((error) => {
    pdfWorkerUrlLoading = null;
    throw error;
  });
  return pdfWorkerUrlLoading;
}

function loadPdfJsLib() {
  if (window.pdfjsLib?.GlobalWorkerOptions?.workerSrc) return Promise.resolve(window.pdfjsLib);
  if (window.__plummePdfJsLoading) return window.__plummePdfJsLoading;
  const scriptLoaded = window.pdfjsLib ? Promise.resolve(window.pdfjsLib) : new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_SCRIPT;
    script.async = true;
    script.integrity = PDFJS_SCRIPT_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.pdfjsLib);
    script.onerror = () => reject(new Error('pdfjs missing'));
    document.head.appendChild(script);
  });
  // Uten workerSrc faller pdf.js tilbake til "fake worker" og parser pa hovedtraden.
  // Bankutskrifter pa mange sider laser da UI-et — merkbart i Safari.
  window.__plummePdfJsLoading = Promise.all([scriptLoaded, loadVerifiedPdfWorkerUrl()])
    .then(([lib, workerUrl]) => {
      if (lib?.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) lib.GlobalWorkerOptions.workerSrc = workerUrl;
      return lib;
    })
    .catch((error) => {
      window.__plummePdfJsLoading = null;
      throw error;
    });
  return window.__plummePdfJsLoading;
}

function PdfViewer({ src, name }) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const docRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [blobUrl, setBlobUrl] = useState(null);
  const pan = useRef(null);

  const onPanDown = (e) => {
    const stage = stageRef.current; if (!stage) return;
    pan.current = { x: e.clientX, y: e.clientY, l: stage.scrollLeft, t: stage.scrollTop };
    stage.classList.add('panning');
    stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
  };
  const onPanMove = (e) => {
    if (!pan.current) return;
    const stage = stageRef.current; if (!stage) return;
    stage.scrollLeft = pan.current.l - (e.clientX - pan.current.x);
    stage.scrollTop = pan.current.t - (e.clientY - pan.current.y);
  };
  const onPanUp = () => { pan.current = null; const s = stageRef.current; if (s) s.classList.remove('panning'); };

  // load document
  useEffect(() => {
    let dead = false, objUrl;
    setStatus('loading'); docRef.current = null; setNumPages(0); setPage(1); setZoom(1); setRot(0); setBlobUrl(null);
    (async () => {
      try {
        if (!src) throw new Error('missing pdf source');
        const sourceUrl = /^https?:\/\//i.test(String(src)) ? new URL(src) : null;
        if (sourceUrl && sourceUrl.origin !== window.location.origin) {
          setStatus('embed');
          return;
        }
        const lib = await loadPdfJsLib();
        if (!lib) {
          setStatus('embed');
          return;
        }
        const res = await fetch(src);
        if (!res.ok) throw new Error('fetch failed');
        const buf = await res.arrayBuffer();
        if (dead) return;
        objUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        setBlobUrl(objUrl);
        // isEvalSupported: false stenger eval-stien i pdf.js (CVE-2024-4367) — vi apner PDF-er brukeren laster opp.
        const doc = await lib.getDocument({ data: new Uint8Array(buf.slice(0)), isEvalSupported: false }).promise;
        if (dead) return;
        docRef.current = doc;
        setNumPages(doc.numPages);
        setStatus('ready');
      } catch (e) {
        if (!dead && src) {
          setStatus('embed');
          return;
        }
        if (!dead) setStatus('error');
      }
    })();
    return () => { dead = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);

  // render current page whenever page/zoom/rotation change
  useEffect(() => {
    if (status !== 'ready' || !docRef.current) return;
    let cancel = false;
    (async () => {
      const doc = docRef.current;
      const pg = await doc.getPage(page);
      if (cancel) return;
      const stageW = (stageRef.current ? stageRef.current.clientWidth : 600) - 36;
      const base = pg.getViewport({ scale: 1, rotation: rot });
      const fit = Math.min(stageW / base.width, 1.7);
      const vp = pg.getViewport({ scale: Math.max(0.2, fit * zoom), rotation: rot });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = vp.width; canvas.height = vp.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      await pg.render({ canvasContext: ctx, viewport: vp }).promise;
    })();
    return () => { cancel = true; };
  }, [status, page, zoom, rot, numPages]);

  if (status === 'embed') return <div className="pdfv">
    <div className="pdfv-bar">
      <div className="pdfv-grp"><span className="pdfv-page">{name || 'PDF'}</span></div>
      <div className="pdfv-grp">
        <a className="pdfv-btn" href={src} target="_blank" rel="noopener noreferrer" aria-label={t('voucher.openNewTab')} title={t('voucher.openNewTab')}><Icon name="eye" size={16} /></a>
        <a className="pdfv-btn" href={src} download={name} aria-label={t('list.download')} title={t('list.download')}><Icon name="download" size={16} /></a>
      </div>
    </div>
    <iframe title={name || 'PDF'} src={src} style={{ width: '100%', minHeight: '72vh', border: 0, background: '#fff', borderRadius: 'var(--r-ms)' }} />
  </div>;

  if (status === 'loading') return <div className="pdfv-msg"><b>{t('shm.pdfLoading')}</b></div>;
  if (status === 'error') return <div className="pdfv-msg"><b>{t('pdf.cannotShow')}</b>
    <span className="muted" style={{ fontWeight: 600 }}>{t('pdf.browserBlocked')}</span>
    {blobUrl && <a className="btn btn-secondary btn-sm" href={blobUrl} download={name} style={{ marginTop: 10 }}><Icon name="download" size={16} />{t('shm.pdfDownloadNamed', { name })}</a>}
  </div>;

  return <div className="pdfv">
    <div className="pdfv-bar">
      <div className="pdfv-grp">
        <button className="pdfv-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label={t('inbox.prevPage')}><Icon name="chevLeft" size={16} /></button>
        <span className="pdfv-page">{t('shm.pdfPage')} <input type="number" min={1} max={numPages} value={page} onChange={(e) => { const v = Math.max(1, Math.min(numPages, Number(e.target.value) || 1)); setPage(v); }} /> / {numPages}</span>
        <button className="pdfv-btn" disabled={page >= numPages} onClick={() => setPage((p) => Math.min(numPages, p + 1))} aria-label={t('inbox.nextPage')}><Icon name="chevRight" size={16} /></button>
      </div>
      <div className="pdfv-grp">
        <button className="pdfv-btn" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.2) * 10) / 10))} aria-label={t('inbox.zoomOut')}>−</button>
        <span className="pdfv-zoom">{Math.round(zoom * 100)}%</span>
        <button className="pdfv-btn" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.2) * 10) / 10))} aria-label={t('inbox.zoomIn')}>+</button>
        <button className="pdfv-btn pdfv-rotate" onClick={() => setRot((r) => (r + 90) % 360)} aria-label={t('pdf.rotate')} title={t('pdf.rotate')}>⟳</button>
        {blobUrl && <a className="pdfv-btn" href={blobUrl} download={name} aria-label={t('list.download')} title={t('list.download')}><Icon name="download" size={16} /></a>}
      </div>
    </div>
    <div className="pdfv-stage" ref={stageRef} onPointerDown={onPanDown} onPointerMove={onPanMove} onPointerUp={onPanUp} onPointerLeave={onPanUp} onDoubleClick={() => setZoom((z) => z > 1 ? 1 : 2)}><canvas ref={canvasRef} className="pdfv-canvas" /></div>
  </div>;
}
Object.assign(window, { PdfViewer });
